import * as amplitude from '@amplitude/analytics-browser';

import { SnowplowService } from 'wanderu-service-analytics';

type Property = Record<string, string | boolean | object | undefined>;

const sendEvent = (
    action: string,
    label: string,
    property: Property
) => SnowplowService.fireStructuredEvent({
    action,
    category: 'react_search',
    label,
    property,
});

const clickToggleMap = (label: string, property: Property) => {
    sendEvent('clicked', label, property);

    amplitude.track(`${label} clicked`, property);
};

interface FilterData {
    isAllowNearbyFiltered: boolean,
    isArrivalStationsFiltered: boolean,
    isArrivalTimingFiltered: boolean,
    isDepartureStationsFiltered: boolean,
    isDepartureTimingFiltered: boolean,
    isDirectOnlyFiltered: boolean,
    isFeaturesFiltered: boolean,
    isTravelModesFiltered: boolean,
}

const getFilterTypes = (filterData: FilterData) => {
    const filterTypes = [];

    if (filterData.isAllowNearbyFiltered) {
        filterTypes.push('nearby_locations');
    }
    if (filterData.isArrivalStationsFiltered) {
        filterTypes.push('arrival_stations');
    }
    if (filterData.isArrivalTimingFiltered) {
        filterTypes.push('arrival_timing');
    }
    if (filterData.isDepartureStationsFiltered) {
        filterTypes.push('departure_stations');
    }
    if (filterData.isDepartureTimingFiltered) {
        filterTypes.push('departure_timing');
    }
    if (filterData.isDirectOnlyFiltered) {
        filterTypes.push('transfers');
    }
    if (filterData.isFeaturesFiltered) {
        filterTypes.push('amenities');
    }
    if (filterData.isTravelModesFiltered) {
        filterTypes.push('travel_mode_or_carriers');
    }
    return filterTypes;
};

interface SearchParams {
    departDate: string;
    departTripId: string;
    destinationPlaceId: string;
    destinationText: string;
    direction: string;
    originPlaceId: string;
    originText: string;
    returnDate?: string;
    returnTripId?: string;
}

const searchPageViewed = ({
    batchId,
    filterData,
    searchParams,
    totalTrips,
}: {
    batchId: string,
    filterData: FilterData,
    searchParams: SearchParams,
    totalTrips: number,
}) => {
    const presetFilterTypes = getFilterTypes(filterData);

    const {
        departDate,
        destinationPlaceId,
        destinationText,
        direction,
        originPlaceId,
        originText,
        returnDate,
    } = searchParams;

    const eventDirection = direction === 'depart'
        ? 'Departing'
        : 'Returning';

    const departTime = new Date(departDate).getTime();
    const tripLengthInDays = returnDate
        ? (new Date(returnDate).getTime() - departTime) / (1000 * 60 * 60 * 24)
        : undefined;

    amplitude.track('[Amplitude] Page Viewed', {
        '[Amplitude] Page Domain': window?.location?.hostname,
        '[Amplitude] Page Location': window?.location?.href,
        '[Amplitude] Page Path': window?.location?.pathname,
        '[Amplitude] Page Title': `${eventDirection} Search Results`,
        '[Amplitude] Page URL': window?.location?.href?.split('?')?.[0],
        arrive_address: destinationText,
        arrive_place_id: destinationPlaceId,
        batch_id: batchId,
        depart_address: originText,
        depart_place_id: originPlaceId,
        is_one_way: !returnDate,
        outbound_date_searched: departDate,
        preset_filter_types: presetFilterTypes,
        referrer: document?.referrer,
        referring_domain: document?.referrer && new URL(document.referrer)?.hostname,
        return_date_searched: returnDate,
        total_results_count: totalTrips,
        trip_length_in_days: tripLengthInDays,
    });
};

const events = {
    clickToggleMap,
    searchPageViewed,
};

export default events;
